'use client';
import { useCallback, useMemo, useState } from 'react';

import { type CryptoFinanceFilter } from '@/services';
import { type CategoryType } from '@/types';
import { getCryptoParams, getFinanceParams } from '@/utils';

import { DEFAULT_FILTERS } from '@/constants';

export const useCryptoFilters = (category: CategoryType) => {
  const [selectedFilter, setSelectedFilter] = useState<CryptoFinanceFilter | null>(null);
  const [sortBy, setSortBy] = useState<string>(DEFAULT_FILTERS.SORT_BY);
  const [status, setStatus] = useState<string>(DEFAULT_FILTERS.STATUS);

  const filterParams = useMemo(() => {
    const params: any = {};

    if (selectedFilter?.tag_id) {
      params.tag_id = selectedFilter.tag_id;
    } else if (category !== 'finance') {
      params.tag_slug = category;
    }

    if (!selectedFilter || selectedFilter.id === 'all') {
      if (category === 'finance') {
        return {
          tag_id: 120,
          end_date_min: new Date().toISOString(),
          archived: false,
          ascending: false,
        };
      }
      return params;
    }

    if (category === 'finance') {
      return getFinanceParams(selectedFilter.label, params);
    }
    return getCryptoParams(selectedFilter.label, params);
  }, [selectedFilter, category]);

  const getSortParams = useCallback((uiSort: string) => {
    switch (uiSort) {
      case DEFAULT_FILTERS.SORT_BY:
        return { sortBy: 'volume24hr' };
      case 'Total Volume':
        return { sortBy: 'volume' };
      case 'Liquidity':
        return { sortBy: 'liquidity' };
      case 'Newest':
        return { sortBy: 'startDate' };
      case 'Ending Soon':
        return { sortBy: 'endDate' };
      case 'Competitive':
        return { sortBy: 'spread' };
      default:
        return { sortBy: 'volume24hr' };
    }
  }, []);

  const sortParams = useMemo(() => getSortParams(sortBy), [sortBy, getSortParams]);

  const handleResetFilters = useCallback(() => {
    setSortBy(DEFAULT_FILTERS.SORT_BY);
    setStatus(DEFAULT_FILTERS.STATUS);
    setSelectedFilter(null);
  }, []);

  const handleFilterClick = useCallback((filter: CryptoFinanceFilter) => {
    setSelectedFilter((prev) => (prev?.id === filter.id ? null : filter));
  }, []);

  return {
    selectedFilter,
    sortBy,
    setSortBy,
    status,
    setStatus,
    filterParams,
    sortParams,
    handleFilterClick,
    handleResetFilters,
  };
};
